import { useDispatch } from 'react-redux';
import { useLocation, NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';

import { useAppSelector } from '../../../../hooks/redux.hook';
import {
  configurationRoutesItems,
  creationRoutesItems,
  helpDeskItems,
  sidebarItems,
  technicalSheetRoutesItems,
} from '../../../../utils/mapping/sidebarItems.mapping';
import {
  layoutSelector,
  onToggleVisibleSidebar,
} from '../../../../app/features/layout/layout.slice';

import SidebarItem from './SidebarItem';
import RoundedIconButton from '../../../../components/global/RoundedIconButton';
import ProdacLogo from '../../../../assets/images/logo-prodac.png';
import ProdiminLogo from '../../../../assets/images/logo-prodimin.png';
import {
  AppPaths,
  AppRoutes,
} from '../../../../utils/mapping/appRoutes.mapping';
import {
  EMPTY_TEXT,
  NO_APPROVE_TICKET,
  NO_ATTEND_TICKET,
  RUTASFAB_MD_DENIED_PERMISSION,
  RUTASFAB_SOL_DENIED_PERMISSION,
} from '../../../../utils/enum/defaultParams.enum';
import { useAuth } from '../../../../hooks/useAuth.hook';

const DashboardSidebar = () => {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const { openSidebar: open } = useAppSelector(layoutSelector);
  const location = useLocation();

  const companyName = user?.sociedad ?? 'PRODAC';

  const onToggleSidebar = () => {
    dispatch(onToggleVisibleSidebar());
  };

  const isuserRutasFabMd = user?.permissions
    ?.toLowerCase()
    .includes(RUTASFAB_MD_DENIED_PERMISSION.toLowerCase());
  const isUserRutasFabSol = user?.permissions
    ?.toLowerCase()
    .includes(RUTASFAB_SOL_DENIED_PERMISSION.toLowerCase());

  // 1. Iniciamos con los items base
  let visibleMenuItems = sidebarItems.filter((m) => m.link);

  // Reemplazo de links dinámicos
  visibleMenuItems.forEach((item) => {
    if (
      item.link === AppPaths.yourReceipt ||
      item.link === AppPaths.performanceAssessment
    ) {
      item.link = item.link.replace('$REPLACE', companyName!.toLowerCase());
    }
  });

  /*Prioridad de Rutas: 
  Es vital que el chequeo de AppRoutes.manufacturingRoutesSkuSheet 
  esté por encima del de AppRoutes.manufacturingRoutes simple. 
  Si no, como la ruta de SKU contiene la palabra "manufacturing-routes", 
  entraría siempre al menú general y nunca verías el de SKU.
  */

  // 2. Lógica de Menús anidados y limpieza de contexto
  if (location.pathname.includes(AppRoutes.helpdesk)) {
    // --- CONTEXTO HELPDESK ---
    visibleMenuItems = helpDeskItems.filter((m) => m.link);

    if (
      user?.permissions?.toLowerCase().includes(NO_ATTEND_TICKET.toLowerCase())
    ) {
      visibleMenuItems = visibleMenuItems.filter(
        (x) => x.link !== AppRoutes.attendTicket,
      );
    }
    if (
      user?.permissions?.toLowerCase().includes(NO_APPROVE_TICKET.toLowerCase())
    ) {
      visibleMenuItems = visibleMenuItems.filter(
        (x) => x.link !== AppRoutes.approveTicket,
      );
    }

    visibleMenuItems.push({
      id: 99, // ID único para evitar choque con el 16
      icon: 'arrow-left',
      label: 'Regresar',
      link: AppRoutes.home,
      //redirectToLabel: requestItem,
      hasPrevDivider: true,
    });
  } else if (
    location.pathname.includes(AppRoutes.manufacturingRoutesSkuSheet)
  ) {
    // --- CONTEXTO SKU SHEETS (Fichas Técnicas) ---
    visibleMenuItems = technicalSheetRoutesItems.filter((m) => m.link);

    // 2. Botón para regresar al menú de fabricación
    visibleMenuItems.push({
      id: 102,
      icon: 'arrow-left',
      label: 'Regresar',
      link: AppRoutes.manufacturingRoutes,
      redirectToLabel: 'Rutas de fabricación',
      hasPrevDivider: true,
    });
  } else if (
    location.pathname.includes(AppRoutes.manufacturingRoutesConfiguration)
  ) {
    // Prioridad alta: Detecta si estamos en configuración antes que en la ruta general de fabricación
    visibleMenuItems = configurationRoutesItems.filter((m) => m.link);

    if (isUserRutasFabSol === false && isuserRutasFabMd === true) {
      visibleMenuItems = visibleMenuItems.filter(
        (x) => x.link === AppRoutes.typeMaterials,
      );
    }

    visibleMenuItems.push({
      id: 100, // ID único
      icon: 'arrow-left',
      label: 'Regresar',
      link: AppRoutes.manufacturingRoutes, // Regresa al menú de solicitudes de fabricación
      redirectToLabel: 'Solicitudes generales',
      hasPrevDivider: true,
    });
  } else if (location.pathname.includes(AppRoutes.manufacturingRoutes)) {
    // --- CONTEXTO FABRICACIÓN GENERAL ---
    visibleMenuItems = creationRoutesItems.filter((m) => m.link);

    if (isUserRutasFabSol === false && isuserRutasFabMd === true) {
      visibleMenuItems = visibleMenuItems.filter(
        (x) => x.link === AppRoutes.requests,
      );
    }

    visibleMenuItems.push({
      id: 101, // ID único
      icon: 'arrow-left',
      label: 'Regresar',
      link: AppRoutes.home,
      redirectToLabel: 'Solicitudes generales',
      hasPrevDivider: true,
    });
  }

  // Filtro de seguridad inicial (fuera de los módulos específicos)
  if (
    isuserRutasFabMd &&
    isUserRutasFabSol &&
    !location.pathname.includes(AppRoutes.manufacturingRoutes)
  ) {
    visibleMenuItems = visibleMenuItems.filter(
      (x) => x.link !== AppRoutes.manufacturingRoutes,
    );
  }

  return (
    <motion.div
      className="w-64 bg-white"
      animate={{ width: open ? '16rem' : '4.5rem' }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex items-center h-20 px-4">
        <motion.div
          animate={{ width: open ? '100%' : '0' }}
          transition={{ duration: !open ? 0.5 : 0 }}
        >
          <NavLink to={AppRoutes.home}>
            {companyName === 'PRODAC' ? (
              <img src={ProdacLogo} alt="Logo Empresa" className="h-8 ml-2" />
            ) : (
              <img
                src={ProdiminLogo}
                alt="Logo Empresa"
                style={{ height: '48px' }}
                className="ml-2"
              />
            )}
          </NavLink>
        </motion.div>
        <div className={`ml-auto ${open ? EMPTY_TEXT : 'mr-auto'}`}>
          <RoundedIconButton
            shadow
            icon={`angles-${open ? 'left' : 'right'}`}
            onClick={onToggleSidebar}
          />
        </div>
      </div>
      <div className="mt-4 px-4">
        <div className="sidebar-no-scroll pb-12">
          {visibleMenuItems.map((m) => (
            /* Usamos una key combinada para asegurar unicidad total en el DOM */
            <SidebarItem key={`${m.id}-${m.label}`} option={m} />
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default DashboardSidebar;
